import {FormGroup, FormLabel} from '@mui/material';
import FormDialog from '../../../Dialog/FormDialog';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import {Asset, ApiFile} from '../../../../types';
import {toast} from 'react-toastify';
import {FormFieldErrors, RemoteErrors} from '@alchemy/react-form';
import {FormRow} from '@alchemy/react-form';
import RenditionDefinitionSelect from '../../../Form/RenditionDefinitionSelect';
import {useTranslation} from 'react-i18next';
import {postRendition} from '../../../../api/rendition';
import {useFormSubmit} from '@alchemy/api';
import {useModals, StackedModalProps, useFormPrompt} from '@alchemy/navigation';

type FormData = {
    definition: string | undefined;
};

type Props = {
    asset: Asset;
    file: ApiFile;
} & StackedModalProps;

export default function SaveFileAsRenditionDialog({
    asset,
    file,
    ...modalProps
}: Props) {
    const {closeModal} = useModals();
    const {t} = useTranslation();

    const {
        control,
        formState: {errors},
        handleSubmit,
        remoteErrors,
        submitting,
        forbidNavigation,
    } = useFormSubmit({
        defaultValues: {
            definition: undefined,
        },
        onSubmit: async (data: FormData) => {
            return await postRendition({
                definitionId: data.definition,
                assetId: asset.id,
                sourceFileId: file.id,
                substituted: true,
            });
        },
        onSuccess: () => {
            toast.success(
                t(
                    'save_file_as_rendition_dialog.rendition_has_been_saved',
                    `Rendition has been saved`
                )
            );
            closeModal();
        },
    });
    useFormPrompt(t, forbidNavigation, modalProps.modalIndex);

    const formId = 'save-file-as-rendition';

    return (
        <FormDialog
            {...modalProps}
            title={t(
                'save_file_as_rendition_dialog.save_file_as_asset_rendition',
                `Save file as asset rendition`
            )}
            loading={submitting}
            formId={formId}
            submitIcon={<FileCopyIcon />}
            submitLabel={t('common.save', `Save`)}
        >
            <form id={formId} onSubmit={handleSubmit}>
                <FormRow>
                    <FormGroup>
                        <FormLabel>
                            {t(
                                'save_file_as_rendition_dialog.rendition_to_add_or_replace',
                                `Rendition to add or replace`
                            )}
                        </FormLabel>
                        <RenditionDefinitionSelect
                            disabled={submitting}
                            name={'definition'}
                            control={control}
                            workspaceId={asset.workspace.id}
                        />
                        <FormFieldErrors field={'definition'} errors={errors} />
                    </FormGroup>
                </FormRow>
            </form>
            <RemoteErrors errors={remoteErrors} />
        </FormDialog>
    );
}
